'use strict';

import axios from 'axios';
import { customEvent } from '@jspartials/general-utils';
import { listenDOMEvent } from '@jspartials/dom-api-utils';
import {
  classes,
  addClassToElement,
  removeClassToElement,
  toggleClassToElement,
  hasClassElement
} from '@jspartials/class-list-utils';
import Toast from '@jscomponents/toast';
import Modal from '@jsclasses/modal-class';
import Recommendations from '@jsclasses/recommendations';
import { addToCartItem } from '@jspartials/cart-utils';
import {
  initQuantityButtons,
  initRemoveProductLineButtons,
  enableSummaryActions,
  disableSummaryActions,
  removeProductLineItem
} from '@jscart/productLineActions';
import { getCartAddonsRecommender } from '@jscart/addonTile';
import { listenAddExtraItem } from '@jscart/addExtraItem';
import {
  initKlarnaMessagingCart,
  updateAmountKlarnaMessaging
} from '@jscomponents/klarnaMessaging';

// Selectors
const selector = {
  page: '.js-cart-page',
  cart: {
    self: '.js-cart',
    summary: {
      self: '.js-cart__summary',
      checkoutButton: '.js-cart__checkout-button',
      shippingText: '.js-cart__shipping-text'
    },
    list: {
      self: '.js-cart__list-wrapper',
      item: {
        self: '.js-cart__list-item',
        action: '.js-cart__list-item-action'
      }
    },
    totals: {
      numberOfProducts: '.js-cart__totals-numberOfProducts',
      grandTotalGross: '.js-cart__totals-grandTotalGross',
      grandTotalWithoutShipping: '.js-cart__totals-grandTotalWithoutShipping',
      grandTotalWithoutShippingWrapper:
        '.js-cart__totals-grandTotalWithoutShippingWrapper',
      discountsWrapper: '.js-cart__totals-discounts-wrapper',
      discounts: '.js-cart__totals-discounts',
      displayButton: '.js-cart__totals-display-button',
      mobileInformation: '.js-cart__totals-mobile-information'
    },
    coupon: {
      wrapper: '.js-cart__coupon-wrapper',
      details: '.js-cart__coupon-details-wrapper',
      form: '.js-cart__promotion-code-form',
      input: '.js-cart__promotion-code-form-input',
      submitButton: '.js-cart__promotion-code-form-submit-button',
      deleteButton: '.js-cart__promotion-code-delete-button',
      errorMessage: '.js-cart__promotion-code-form-error-message'
    }
  },
  wrapper: {
    recommendations: '.js-cart__recommendations',
    cart: '.js-cart-wrapper',
    error: '.js-cart__error-wrapper',
    freeShipping: '.js-cart__free-shipping-wrapper'
  },
  constants: {
    hasGross: 'has-gross',
    hasPromotion: 'has-promotion'
  }
};

// Elements
const element = {
  wrapper: {
    recommendations:
      document.querySelectorAll(selector.wrapper.recommendations) || []
  }
};

// Constants
const cart = document.querySelector(selector.cart.self) || null;
const cartGrandTotalWithoutShipping =
  document.querySelector(selector.cart.totals.grandTotalWithoutShipping) ||
  null;
const cartGrandTotalDiscounts =
  document.querySelector(selector.cart.totals.discounts) || null;
const cartGrandTotalDiscountsWrapper =
  document.querySelector(selector.cart.totals.discountsWrapper) || null;
const cartCouponDetailsWrapper =
  document.querySelector(selector.cart.coupon.details) || null;

const updateDonations = new CustomEvent('updateDonationsFromCart');
const defaultErrorMessage = cart?.dataset?.defaultErrorMessage;
const TOAST_TYPE = 'error';
// Variables
var isCartFull = cart?.dataset?.full === 'true';
var cartPromotionCodeFormErrorMessage;
var cartPromotionCodeFormInput;
var cartPromotionCodeForm;
var cartPromotionCodeFormSubmitButton;
var cartPromotionCodeFormDeletePromoButton;

// Functions

function updateCart(data) {
  if (data.basketModel.numItems != 0) {
    updateListProductLineItems(data);
  } else {
    var cartWrapper = document.querySelector(selector.wrapper.cart);
    cartWrapper.dataset.empty = true;
    showEmptyCart();
  }
}

function showEmptyCart() {
  axios
    .get(globalVariables.endpoint.showEmptyCart)
    .then(function (response) {
      if (response.status === 200) {
        const data = response.data;
        const cart = document.querySelector(selector.cart.self);
        cart.innerHTML = data;
      }
    })
    .catch((error) => {
      console.error(error);
    });
}

function updateCartError(validationStatus) {
  const cartError = document.querySelector(selector.wrapper.error);
  var checkoutButton = document.querySelector(
    selector.cart.summary.checkoutButton
  );

  if (validationStatus.hasError && validationStatus.message.trim() !== '') {
    cartError.innerHTML = validationStatus.message.trim();
    checkoutButton.disabled = true;
    removeClassToElement(cartError, classes.is.hidden);
  } else {
    cartError.innerHTML = '';
    checkoutButton.disabled = false;
    addClassToElement(cartError, classes.is.hidden);
  }
}

function updateListProductLineItems(data) {
  const cartItemsListWrapper = document.querySelector(selector.cart.list.self);
  if (cartItemsListWrapper) {
    cartItemsListWrapper.innerHTML = data.listHtml;
  }
  initQuantityButtons(cart);
  initRemoveProductLineButtons(cart);
  updateTotalCost(data.basketModel);
  updateAmountKlarnaMessaging(data.basketModel.totals.klarnaTotal);
  updateCouponCode(data.basketModel.totals);

  Modal.initElements('cart');
  document.dispatchEvent(updateDonations);

  enableSummaryActions(cart);

  updateCartError(data.basketModel.validationStatus);
}

function validateCouponCode(e) {
  e.preventDefault();

  if (hasClassElement(cartPromotionCodeForm, classes.is.invalid)) {
    removeClassToElement(cartPromotionCodeForm, classes.is.invalid);
  }
  const promotionCodeValue = cartPromotionCodeFormInput.value.trim();

  if (promotionCodeValue === '') {
    setInvalidPromotionCode(
      cartPromotionCodeFormSubmitButton.dataset.errorEmpty
    );
  } else {
    const endpointValidateCouponCode =
      globalVariables.endpoint.submitCouponCode;

    const formData = new FormData(cartPromotionCodeForm);
    addClassToElement(cartPromotionCodeFormSubmitButton, classes.is.loading);
    axios
      .post(endpointValidateCouponCode, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      .then(function (response) {
        if (response.status === 200) {
          const data = response.data;

          if (data.error) {
            setInvalidPromotionCode(data.errorMessage);
          } else {
            document.dispatchEvent(
              new CustomEvent('refreshCart', {
                detail: {
                  data: data
                }
              })
            );
          }
        }
      })
      .catch(function (error) {
        Toast(defaultErrorMessage, TOAST_TYPE);
        console.error(error);
      })
      .then(function () {
        removeClassToElement(
          cartPromotionCodeFormSubmitButton,
          classes.is.loading
        );
      });
  }
}

function updateCouponCode(totals) {
  var appliedDiscount = null;
  totals.discounts.forEach((discount) => {
    if (discount.type === 'coupon' && discount.applied) {
      appliedDiscount = discount;
    }
  });
  var couponWrapper = document.querySelector(selector.cart.coupon.wrapper);
  if (couponWrapper) {
    couponWrapper.innerHTML = totals.discountsHtml;
  }

  initPromotionCodeSubmit();
  if (totals.orderLevelDiscountTotal.value > 0) {
    removeClassToElement(cartGrandTotalDiscountsWrapper, classes.hidden);
    cartGrandTotalDiscounts.innerText = `- ${totals.orderLevelDiscountTotal.formatted}`;
  } else {
    addClassToElement(cartGrandTotalDiscountsWrapper, classes.hidden);
    cartGrandTotalDiscounts.innerText = '';
  }
  if (appliedDiscount) {
    cartPromotionCodeFormDeletePromoButton.dataset.uuid = appliedDiscount.UUID;

    cartPromotionCodeFormDeletePromoButton.dataset.code =
      appliedDiscount.couponCode;
  }
}

function updateTotalCost(data) {
  const totals = data.totals;
  const cartShippingWrappers =
    document.querySelectorAll(selector.wrapper.freeShipping) || null;
  const cartGrandTotalWithoutShippingWrapper =
    document.querySelector(
      selector.cart.totals.grandTotalWithoutShippingWrapper
    ) || null;
  const cartGrandTotalGross =
    document.querySelector(selector.cart.totals.grandTotalGross) || null;
  const cartMobileInformation =
    document.querySelector(selector.cart.totals.mobileInformation) || null;
  const freeShippingContainerMobile =
    '.js-cart__free-shipping-container-mobile';
  const freeShippingContainerDesktop =
    '.js-cart__free-shipping-container-desktop';

  if (cartGrandTotalWithoutShippingWrapper && cartMobileInformation) {
    if (totals.showMobileInformation) {
      addClassToElement(
        cartGrandTotalWithoutShippingWrapper,
        selector.constants.hasPromotion
      );
    } else {
      removeClassToElement(
        cartGrandTotalWithoutShippingWrapper,
        selector.constants.hasPromotion
      );
      removeClassToElement(
        cartGrandTotalWithoutShippingWrapper,
        classes.is.visible
      );
      removeClassToElement(cartMobileInformation, classes.is.visible);
    }
  }

  if (cartGrandTotalWithoutShipping) {
    cartGrandTotalWithoutShipping.innerText = totals.grandTotalWithoutShipping;
    if (totals.showGrossPrice) {
      addClassToElement(
        cartGrandTotalWithoutShipping,
        selector.constants.hasGross
      );
    } else {
      removeClassToElement(
        cartGrandTotalWithoutShipping,
        selector.constants.hasGross
      );
    }
  }

  if (cartGrandTotalGross) {
    cartGrandTotalGross.innerText = totals.merchandizeTotalGrossPrice;
    if (totals.showGrossPrice) {
      removeClassToElement(cartGrandTotalGross, classes.hidden);
    } else {
      addClassToElement(cartGrandTotalGross, classes.hidden);
    }
  }
  if (
    Object.keys(totals.shippingDiscount).length > 0 &&
    cartShippingWrappers.length > 0
  ) {
    cartShippingWrappers.forEach((cartShippingWrapper) => {
      const cartShippingText = cartShippingWrapper.querySelector(
        selector.cart.summary.shippingText
      );
      cartShippingText.innerText = totals.shippingDiscount.lineItemText;
      removeClassToElement(
        cartShippingWrapper.closest(freeShippingContainerDesktop),
        'md:tw-hidden'
      );
      removeClassToElement(
        cartShippingWrapper.closest(freeShippingContainerMobile),
        classes.hidden
      );
    });
  } else if (cartShippingWrappers.length > 0) {
    cartShippingWrappers.forEach((cartShippingWrapper) => {
      addClassToElement(
        cartShippingWrapper.closest(freeShippingContainerDesktop),
        'md:tw-hidden'
      );
      addClassToElement(
        cartShippingWrapper.closest(freeShippingContainerMobile),
        classes.hidden
      );
    });
  }
}

function removeCouponCode() {
  const endpointDeleteCouponLineItem =
    globalVariables.endpoint.removeCouponLineItem;

  addClassToElement(cartPromotionCodeFormDeletePromoButton, classes.is.loading);

  axios
    .get(endpointDeleteCouponLineItem, {
      params: {
        uuid: cartPromotionCodeFormDeletePromoButton.dataset.uuid
      }
    })
    .then(function (response) {
      if (response.status === 200) {
        const data = response.data;
        document.dispatchEvent(
          new CustomEvent('refreshCart', {
            detail: {
              data: data
            }
          })
        );
      }
    })
    .catch(function (error) {
      Toast(defaultErrorMessage, TOAST_TYPE);
      console.error(error);
    })
    .then(function () {
      removeClassToElement(
        cartPromotionCodeFormDeletePromoButton,
        classes.is.loading
      );
    });
}

function setInvalidPromotionCode(errorMessage) {
  cartPromotionCodeFormErrorMessage.innerText = errorMessage;
  addClassToElement(cartPromotionCodeForm, classes.is.invalid);
}

function initPromotionCodeSubmit() {
  cartPromotionCodeFormErrorMessage = document.querySelector(
    selector.cart.coupon.errorMessage
  );
  cartPromotionCodeFormInput = document.querySelector(
    selector.cart.coupon.input
  );
  cartPromotionCodeForm = document.querySelector(selector.cart.coupon.form);
  cartPromotionCodeFormSubmitButton = document.querySelector(
    selector.cart.coupon.submitButton
  );
  cartPromotionCodeFormDeletePromoButton = document.querySelector(
    selector.cart.coupon.deleteButton
  );

  listenDOMEvent(cartPromotionCodeForm, 'submit', validateCouponCode);
  listenDOMEvent(cartPromotionCodeFormInput, 'input', function () {
    if (hasClassElement(cartPromotionCodeForm, classes.is.invalid)) {
      removeClassToElement(cartPromotionCodeForm, classes.is.invalid);
    }
  });

  listenDOMEvent(
    cartPromotionCodeFormDeletePromoButton,
    'click',
    removeCouponCode
  );
}

function initRecommendations() {
  element.wrapper.recommendations.forEach((wrapperRecommendations) => {
    new Recommendations({
      wrapper: wrapperRecommendations,
      showPrice: true,
      maxVisibleItems: 4
    });
  });
}

function initMobileInformation() {
  const cartMobileInformation = document.querySelector(
    selector.cart.totals.mobileInformation
  );
  const cartGrandTotalWithoutShippingWrapper = document.querySelector(
    selector.cart.totals.grandTotalWithoutShippingWrapper
  );
  const cartMobileInformationButton = document.querySelector(
    selector.cart.totals.displayButton
  );
  if (cartMobileInformation && cartGrandTotalWithoutShippingWrapper) {
    listenDOMEvent(cartMobileInformationButton, 'click', function () {
      toggleClassToElement(cartMobileInformation, classes.is.visible);
      toggleClassToElement(
        cartGrandTotalWithoutShippingWrapper,
        classes.is.visible
      );
    });
  }
}

function initCart() {
  initQuantityButtons(cart);
  initRemoveProductLineButtons(cart);
  initPromotionCodeSubmit();
  initMobileInformation();
  initKlarnaMessagingCart();

  // Add padding bottom in mobile

  const cartSummary = document.querySelector(selector.cart.summary.self);

  if (cartSummary) {
    const cartPage = document.querySelector(selector.page);
    const cartSummaryHeight = `${cartSummary.offsetHeight}px`;
    cartPage.style.paddingBottom = cartSummaryHeight;
  }

  Modal.initElements('cart'); // Load cart lineitems modals

  isCartFull = cart?.dataset?.full === 'true';
}

// Init

listenDOMEvent(document, 'DOMContentLoaded', function () {
  initRecommendations();

  if (cart) {
    initCart();

    listenDOMEvent(document, 'addDonationToCart', function (e) {
      const itemPID = e.detail.itemPID;
      isCartFull = cart?.dataset?.full === 'true';

      if (!isCartFull) {
        addToCartItem(itemPID, 1, '', []);
      }
    });

    listenDOMEvent(document, 'removeDonationFromCart', function (e) {
      const itemPID = e.detail.itemPID;
      const itemUUID = e.detail.itemUUID;
      const target = e.detail.target;
      const wrapper = e.detail.wrapper;

      removeProductLineItem(itemPID, itemUUID, target, wrapper);
    });
    listenDOMEvent(document, 'refreshCart', function (e) {
      updateCart(e.detail.data);
    });
  }

  listenDOMEvent(document, 'afterAddToCartShow', function (e) {
    const cartWrapper = document.querySelector(selector.wrapper.cart);

    if (cartWrapper) {
      if (cartWrapper.dataset.empty === 'true') {
        location.reload();
      } else {
        updateCart(e.detail.data);
      }
    }
  });

  listenDOMEvent(document, 'cartActionProcessing', function () {
    disableSummaryActions(cart);
  });

  listenDOMEvent(document, 'cartActionProcessed', function () {
    enableSummaryActions(cart);
  });

  listenDOMEvent(document, 'cartError', function (e) {
    if (e.detail.error !== 'cartIsFull') {
      Toast(defaultErrorMessage, TOAST_TYPE);
    }
  });

  document.addEventListener(customEvent.listenAddonTiles, () => {
    getCartAddonsRecommender();
  });

  listenAddExtraItem();
});
